import { ModelRow } from "@mcleod/core";
import { SecondaryFilterProps } from "../FilterPanel";
import { LMECompanySettings } from "@mcleod/general";
import { PlanningUtil, TempWarnings } from "../PlanningUtil";

export class DriverManagementSecondaryFilter {
    static readonly FILTER_UNASSIGNED: SecondaryFilterProps = {
        id: "unassignedDrivers",
        caption: "Unassigned",
        filter: (row: ModelRow) => row.get("driver1_preassign_count", 0) < 1
    };

    static readonly FILTER_PROJECTED_LATE: SecondaryFilterProps = {
        id: "projectedLate",
        caption: "Tracking Late",
        filter: (row: ModelRow) => row.getBoolean("projected_late", false),
    };

    static readonly FILTER_APPOINTMENT_LATE: SecondaryFilterProps = {
        id: "isLate",
        caption: "Appointment Late",
        filter: (row: ModelRow) => { return PlanningUtil.isLateWarning(row, "next_stop");
        }
    }

    static readonly FILTER_ARRIVED_AT_SHIPPER = {
        id: "arrivedAtShipper",
        caption: "Arrived At Shipper",
        filter: (row: ModelRow) => {
            return this.arrivedAtPreassignment(row);
        }
    };

    // For delivered moves on driver management, the next stop is going to be the first stop on the next preassignment
    private static arrivedAtPreassignment(row: ModelRow) {
        return row.getString("move_status_desc", null) === "Delivered"
        && !row.isNull("next_stop_actual_arrival")
        && row.isNull("next_stop_actual_departure")
    }

    static readonly FILTER_ARRIVED_AT_CONSIGNEE = {
        id: "arrivedAtConsignee",
        caption: "Arrived At Consignee",
        filter: (row: ModelRow) => {
            return !row.isNull("destination_actual_arrival")
            && row.isNull("destination_actual_departure")
        }
    };

    static readonly FILTER_UNSENT = {
        id: "unsent",
        caption: "Unsent",
        filter: (row: ModelRow) => {
            const preassignCount = row.getNumber("driver1_preassign_count", 0);
            const npXmitted = row.getBoolean("np_xmitted");
            return (preassignCount > 0 && !npXmitted);
        },
        visible: (data: any) => {
            return LMECompanySettings.get().mobile_comm_licensed_active;
        }
    };

    static readonly FILTER_UNACCEPTED = {
        id: "unaccepted",
        caption: "Unaccepted",
        filter: (row: ModelRow) => {
            const preassignCount = row.getNumber("driver1_preassign_count", 0);
            const npXmitted = row.getBoolean("np_xmitted");
            const npAcknowledged = row.getString("np_acknowledged");
            return (preassignCount > 0 && npXmitted && npAcknowledged != "Y");
        },
        visible: (data: any) => {
            return LMECompanySettings.get().mobile_comm_licensed_active;
        }
    };

    static readonly FILTER_UNSEATED_DRIVER = {
        id: "unseatedDriver",
        caption: "Unseated Driver",
        filter: (row: ModelRow) => {
            return row.isNull("tractor") && row.getBoolean("driver1_active", false);
        }
    };

    static readonly FILTER_TEMP_WARNINGS = {
        id: "trailerTempWarnings",
        caption: "Trailer Temp Warnings",
        filter: (row: ModelRow) => {
            const result = PlanningUtil.getTempWarnings(row);
            return (result.return_air != null && result.return_air == TempWarnings.EXCEEDS)
                || (result.setpoint != null && result.setpoint == TempWarnings.EXCEEDS);
        }
    }

    static readonly FILTER_MC_MESSAGES: SecondaryFilterProps = {
        id: "mcMessages",
        caption: "MC Messages",
        filter: (row: ModelRow) => {
            return row.get("mc_message_count", 0) > 0;
        }
    }

    static readonly FILTER_FREE_TIME_STARTED = {
        id: "freeTimeStarted",
        caption: "Free Time Started",
        licensedPackage: "com.tms.client.loadmaster.det",
        filter: (row: ModelRow) =>
            "A" !== row.getString("move_status", "")
            && ("F" === row.getString("origin_detention", "")
                || "F" === row.getString("destination_detention", "")
                || "F" === row.getString("next_stop_detention", "")
                || row.get("stop_detention_statuses", []).includes("F"))
    }

    static readonly FILTER_DETENTION_CHARGING = {
        id: "detentionCharging",
        caption: "Detention Charging",
        licensedPackage: "com.tms.client.loadmaster.det",
        filter: (row: ModelRow) =>
            "A" !== row.getString("move_status", "")
            && ("B" === row.getString("origin_detention", "")
                || "B" === row.getString("destination_detention", "")
                || "B" === row.getString("next_stop_detention", "")
                || row.get("stop_detention_statuses", []).includes("B"))
    }

    static readonly FILTER_DETENTION_OCCURRED = {
        id: "detentionOccurred",
        caption: "Detention Occurred",
        licensedPackage: "com.tms.client.loadmaster.det",
        filter: (row: ModelRow) =>
            "A" !== row.getString("move_status", "")
            && ("D" === row.getString("origin_detention", "")
                || "D" === row.getString("destination_detention", "")
                || "D" === row.getString("next_stop_detention", "")
                || row.get("stop_detention_statuses", []).includes("D"))
    }

    static readonly FILTER_DETENTION_CLOSED = {
        id: "detentionClosed",
        caption: "Detention Closed",
        licensedPackage: "com.tms.client.loadmaster.det",
        filter: (row: ModelRow) =>
            "A" !== row.getString("move_status", "")
            && ("C" === row.getString("origin_detention", "")
                || "C" === row.getString("destination_detention", "")
                || "C" === row.getString("next_stop_detention", "")
                || row.get("stop_detention_statuses", []).includes("C"))
    }
}
